import { gql, useQuery } from '@apollo/client';
import { Stack, Text } from '@chakra-ui/react';
import * as React from 'react';

import { NavigationButton } from '@/components/NavigationButton';
import { DateWindowType, SortOptions } from '@/graphql-types/globalTypes';
import { getAppropriateEventLink } from '@/hooks/getAppropriateEventLink';
import { BookmarkIcon } from '@/imports/ui/chakra/feather';

import {
  MyEvents_Events,
  MyEvents_EventsVariables,
} from './graphql-types/MyEvents_Events';

const EVENTS = gql`
  query MyEvents_Events($input: EventsInput!) {
    events(input: $input) {
      results {
        _id
        name
        ...GetAppropriateEventLink_EventFragment
      }
    }
  }
  ${getAppropriateEventLink.eventFragment}
`;

export const MyEvents: React.FC<{
  filter: 'upcoming' | 'past';
}> = (props) => {
  const today = React.useRef(new Date());

  const { loading, error, data } = useQuery<
    MyEvents_Events,
    MyEvents_EventsVariables
  >(EVENTS, {
    variables: {
      input: {
        limit: { upcoming: 5, past: 3 }[props.filter],
        offset: 0,
        filter: {
          includeCurrentUserInvolvedEventsOnly: true,
          dateWindow: {
            windowType:
              props.filter === 'upcoming'
                ? DateWindowType.AnyOverlap
                : DateWindowType.EndsWithin,
            windowStart:
              props.filter === 'upcoming'
                ? today.current.toISOString()
                : undefined,
            windowEnd:
              props.filter === 'past' ? today.current.toISOString() : undefined,
          },
        },
        sort: {
          startDate: {
            upcoming: SortOptions.asc,
            past: SortOptions.desc,
          }[props.filter],
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  if (!data?.events.results.length) {
    return null;
  }

  return (
    <Stack spacing="4">
      <Text
        textStyle="compactStrong"
        color="gray.600"
        textTransform="uppercase"
        px="4"
      >
        {
          {
            upcoming: 'My Upcoming Events',
            past: 'My Recent Past Events',
          }[props.filter]
        }
      </Text>
      <Stack>
        {data.events.results.map((event) => (
          <NavigationButton
            key={event._id}
            href={getAppropriateEventLink(event)}
            leftIcon={<BookmarkIcon />}
            size="md"
          >
            {event.name}
          </NavigationButton>
        ))}
      </Stack>
    </Stack>
  );
};
